import { useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Search, Loader2, AlertTriangle, Globe, TrendingDown, Truck, Wifi,
  FileText, ChevronDown, ChevronUp, PackagePlus, ExternalLink, Package,
  CheckCircle2, AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { DataTable, EmptyState, ErrorState, Button, ConfirmDialog, type ColumnDef, type Row } from '@/components/ui'
import { getErrorMessage } from '../shared.ts'
import { useDebounce } from '@/hooks/useDebounce.ts'

// ─── EGIS Search Section ────────────────────────────────────────────────────

interface EgisSearchItem {
  proprietaryProductNumber: string
  description: string
  manufacturerName: string
  manufacturerId: number
  manufacturerProductNumber: string
  globalProductNumber: string | null
  productGroupId: number
  purchasePrice: number | null
  currencyCode: string
  priceDateTime: string | null
  recommendedRetailPrice: number | null
  imageUrl: string | null
}

interface EgisSearchResult {
  totalResults: number
  firstResult: number
  lastResult: number
  items: EgisSearchItem[]
}

interface EgisDistributorOffer {
  distributorName: string
  distributorId: string
  distributorProductNumber: string
  productDescription: string
  availableQuantity: number
  purchasePrice: number | null
  recommendSellingPrice: number | null
  netSalesPrice: number | null
  retailPrice: number | null
  currencyCode: string
  eol: boolean
  deliveryInfo: {
    standardDeliveryTimeHours: number | null
    expectedDeliveryDate: string | null
    deliveryDateText: string | null
    freightCharges: number | null
  } | null
}

interface EgisPriceAndAvailability {
  productNumber: string
  description: string
  manufacturerName: string
  globalProductNumber: string | null
  distributorOffers: EgisDistributorOffer[]
}

interface EgisProductSpecification {
  productNumber: string
  description: string
  manufacturerName: string
  manufacturerId: number
  manufacturerProductNumber: string
  globalProductNumber: string | null
  productGroupId: number
  contentLevel: string | null
  imageUrl: string | null
  features: Array<{ group: string | null; key: string; value: string }>
  additionalImages: Array<{
    url: string
    width: number | null
    height: number | null
    type: string | null
    source: string | null
  }>
  datasheetUrl: string | null
  datasheetPdfUrl: string | null
  descriptions: {
    short: string | null
    shortSummary: string | null
    longSummary: string | null
  }
  marketingText: string | null
  accessories: string[]
}

interface EgisTestResult {
  success: boolean
  baseUrl: string
  operationUrl: string
  login: string
  responseBody?: string
  attempts?: Array<{ contentType: string; httpStatus: number; ok: boolean; responseBody: string }>
}

export function EgisSearchSection() {
  const [query, setQuery] = useState('')
  const [onlyActive, setOnlyActive] = useState(true)
  const [onlyStocked, setOnlyStocked] = useState(false)
  const [startRow, setStartRow] = useState(1)
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<EgisTestResult | null>(null)
  const [testLoading, setTestLoading] = useState(false)
  const [confirmSap, setConfirmSap] = useState(false)
  const [sapResult, setSapResult] = useState<{ success: boolean; itemCode?: string; message?: string } | null>(null)
  // Tracks stock availability per product number once price-availability data has been loaded
  const [stockStatus, setStockStatus] = useState<Map<string, boolean>>(new Map())

  // Debounce: search fires at most once every 1.5s
  const debouncedQuery = useDebounce(query, 1500)

  const searchQuery = useQuery<EgisSearchResult>({
    queryKey: ['egis-search', debouncedQuery, onlyActive, onlyStocked, startRow],
    queryFn: async () => {
      const params = new URLSearchParams({ q: debouncedQuery })
      if (onlyActive) params.set('onlyActive', 'true')
      if (onlyStocked) params.set('onlyStocked', 'true')
      if (startRow > 1) params.set('startRow', String(startRow))
      const { data } = await apiClient.get(`/egis/search?${params}`)
      return data
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
  })

  // Derived from searchQuery.data — must be declared before pagePriceQuery which uses it in queryKey/enabled
  const result = searchQuery.data

  const priceQuery = useQuery<EgisPriceAndAvailability[]>({
    queryKey: ['egis-price', selectedProduct],
    queryFn: async () => {
      const { data } = await apiClient.get(`/egis/price-availability?products=${selectedProduct}`)
      return data
    },
    enabled: !!selectedProduct,
    staleTime: 2 * 60 * 1000,
  })

  const hasMore = result ? result.lastResult < result.totalResults : false
  const hasPrev = startRow > 1
  const isTyping = query !== debouncedQuery && query.length >= 2

  // Batch-fetch stock status for up to 10 products on the current results page (EGIS API limit: max 10 per request)
  const pageProductNumbers = (result?.items ?? []).slice(0, 10).map((i) => i.proprietaryProductNumber).join(',')
  const pagePriceQuery = useQuery<EgisPriceAndAvailability[]>({
    queryKey: ['egis-page-prices', pageProductNumbers],
    queryFn: async () => {
      const { data } = await apiClient.get(`/egis/price-availability?products=${pageProductNumbers}`)
      return data
    },
    enabled: pageProductNumbers.length > 0,
    staleTime: 2 * 60 * 1000,
  })

  const specQuery = useQuery<EgisProductSpecification[]>({
    queryKey: ['egis-spec', selectedProduct],
    queryFn: async () => {
      const { data } = await apiClient.get(`/egis/product-spec?products=${selectedProduct}`)
      return data
    },
    enabled: !!selectedProduct,
    staleTime: 10 * 60 * 1000,
  })

  const createInSapMutation = useMutation({
    mutationFn: async ({ productNumber, salesPrice }: { productNumber: string; salesPrice?: number }) => {
      const { data } = await apiClient.post('/egis/create-in-sap', { productNumber, salesPrice })
      return data as { success: boolean; itemCode: string; itemName: string }
    },
    onSuccess: (data) => {
      setSapResult({ success: true, itemCode: data.itemCode, message: `Artikel ${data.itemCode} in SAP angelegt.` })
      setConfirmSap(false)
    },
    onError: (err: Error) => {
      setSapResult({ success: false, message: getErrorMessage(err) })
      setConfirmSap(false)
    },
  })

  // Populate stock status from single-product price query (on expand)
  useEffect(() => {
    if (priceQuery.data && selectedProduct) {
      const offers = priceQuery.data[0]?.distributorOffers ?? []
      const hasStock = offers.some((o) => (o.availableQuantity ?? 0) > 0)
      setStockStatus((prev) => new Map(prev).set(selectedProduct, hasStock))
    }
  }, [priceQuery.data, selectedProduct])

  // Populate stock status from batch price query for all page results
  useEffect(() => {
    if (pagePriceQuery.data) {
      setStockStatus((prev) => {
        const next = new Map(prev)
        for (const p of pagePriceQuery.data) {
          const hasStock = p.distributorOffers.some((o) => (o.availableQuantity ?? 0) > 0)
          next.set(p.productNumber, hasStock)
        }
        return next
      })
    }
  }, [pagePriceQuery.data])

  async function runConnectionTest() {
    setTestLoading(true)
    setTestResult(null)
    try {
      const { data } = await apiClient.get<EgisTestResult>('/egis/test?q=hp')
      setTestResult(data)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setTestResult({ success: false, baseUrl: '?', operationUrl: '?', login: '?', responseBody: msg })
    } finally {
      setTestLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setStartRow(1)
    setSelectedProduct(null)
    setSapResult(null)
  }

  const spec = specQuery.data?.[0] ?? null
  const priceData = priceQuery.data?.[0] ?? null

  // Whether the selected product is visible in the current search results page
  const selectedItemInResults = result?.items.find((i) => i.proprietaryProductNumber === selectedProduct) ?? null
  const selectedItem = selectedItemInResults

  const searchColumns = useMemo<ColumnDef<EgisSearchItem, unknown>[]>(
    () => [
      {
        accessorKey: 'description',
        header: 'Artikel',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-gray-900 truncate max-w-xs" title={row.original.description}>
              {row.original.description}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {row.original.proprietaryProductNumber} · {row.original.manufacturerProductNumber}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'manufacturerName',
        header: 'Hersteller',
      },
      {
        accessorKey: 'globalProductNumber',
        header: 'EAN',
        cell: ({ row }) => (
          <span className="text-xs text-gray-500 font-mono">{row.original.globalProductNumber || '–'}</span>
        ),
      },
      {
        id: 'stock',
        header: 'Lager',
        enableSorting: false,
        cell: ({ row }) => {
          const status = stockStatus.get(row.original.proprietaryProductNumber)
          if (status === undefined) {
            return pagePriceQuery.isLoading
              ? <span className="flex items-center justify-center"><Loader2 className="w-3 h-3 animate-spin text-gray-300" /></span>
              : <span className="block text-center text-gray-300 text-xs">–</span>
          }
          return status ? (
            <span className="flex items-center justify-center gap-1 text-green-600 text-xs font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Lagernd
            </span>
          ) : (
            <span className="flex items-center justify-center gap-1 text-amber-600 text-xs font-medium">
              <AlertCircle className="w-3.5 h-3.5" />
              Kein Lager
            </span>
          )
        },
      },
      {
        accessorKey: 'purchasePrice',
        header: 'EK',
        cell: ({ row }) => (
          <span className="block text-right font-mono text-gray-900">
            {row.original.purchasePrice != null ? `${row.original.purchasePrice.toFixed(2)} €` : '–'}
          </span>
        ),
      },
      {
        accessorKey: 'recommendedRetailPrice',
        header: 'UVP',
        cell: ({ row }) => (
          <span className="block text-right font-mono text-gray-500">
            {row.original.recommendedRetailPrice != null ? `${row.original.recommendedRetailPrice.toFixed(2)} €` : '–'}
          </span>
        ),
      },
    ],
    [stockStatus, pagePriceQuery.isLoading],
  )

  // Inline expanded row — rendered directly under the article row in the table
  const renderExpandedRow = useMemo(
    () => (row: Row<EgisSearchItem>) => {
      const isLoading = priceQuery.isLoading || specQuery.isLoading
      if (isLoading) {
        return (
          <div className="flex items-center gap-2 text-xs text-gray-500 py-3 px-1">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Preise & Verfügbarkeit werden geladen...
          </div>
        )
      }
      if (!spec && !priceData) return <div />
      return (
        <EgisDetailPanel
          item={row.original}
          spec={spec}
          priceData={priceData}
          priceLoading={priceQuery.isLoading}
          specLoading={specQuery.isLoading}
          onCreateInSap={() => { setSapResult(null); setConfirmSap(true) }}
          sapResult={sapResult}
          onSelectAccessory={(pn) => { setSelectedProduct(pn); setSapResult(null) }}
        />
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spec, priceData, priceQuery.isLoading, specQuery.isLoading, sapResult],
  )

  // Expand IDs: only expand in the table if the product is in the current results page.
  // If the product is an accessory not in results, fall through to the below-table panel.
  const expandedRowIds = selectedProduct && selectedItemInResults ? [selectedProduct] : []

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-brand-500" />
            <h3 className="text-sm font-semibold text-gray-900">EGIS Artikelsuche</h3>
            <span className="text-xs text-gray-400">Distributoren-Katalog durchsuchen</span>
          </div>
          <button
            type="button"
            onClick={runConnectionTest}
            disabled={testLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            {testLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
            Verbindungstest
          </button>
        </div>

        <form onSubmit={handleSearch} className="flex gap-3 items-end">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setStartRow(1); setSelectedProduct(null); setSapResult(null) }}
                placeholder="Artikelname, EAN, Hersteller-Nr. eingeben (min. 2 Zeichen)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              {isTyping && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-gray-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  sucht in 1.5s…
                </div>
              )}
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} className="rounded border-gray-300" />
            Nur aktive
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={onlyStocked} onChange={(e) => setOnlyStocked(e.target.checked)} className="rounded border-gray-300" />
            Nur lagernd
          </label>
          <button
            type="submit"
            disabled={query.length < 2}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Search className="w-4 h-4" />
            Suchen
          </button>
        </form>
      </div>

      {testResult && (
        <div className={cn(
          'rounded-lg border p-4 text-sm space-y-2',
          testResult.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800',
        )}>
          <div className="flex items-center gap-2 font-medium">
            {testResult.success
              ? <><span className="text-green-600">✓</span> Verbindung erfolgreich</>
              : <><AlertTriangle className="w-4 h-4 text-red-500" /> Verbindung fehlgeschlagen</>}
          </div>
          <div className="font-mono text-xs space-y-0.5 text-gray-700">
            <div><span className="text-gray-500">Base URL:</span> {testResult.baseUrl}</div>
            <div><span className="text-gray-500">API-Endpoint:</span> {testResult.operationUrl}</div>
            <div><span className="text-gray-500">Login:</span> {testResult.login}</div>
          </div>
          {!testResult.success && testResult.responseBody && (
            <details className="text-xs">
              <summary className="cursor-pointer text-red-700 font-medium">Details anzeigen</summary>
              <pre className="mt-2 p-2 bg-white border border-red-200 rounded overflow-auto max-h-40 text-red-700 whitespace-pre-wrap">{testResult.responseBody}</pre>
            </details>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Wenn der API-Endpoint 404 zurückgibt: Bitte die Base-URL in den Systemeinstellungen → Adapter → EGIS prüfen.
            Die konfigurierte URL muss zum EGIS-Portal-Zugang passen (z.B. <code>https://www.egis-online.de</code>).
          </p>
        </div>
      )}

      {searchQuery.isError && (
        <ErrorState
          size="sm"
          title="EGIS-Fehler"
          description={getErrorMessage(searchQuery.error as Error)}
          details={searchQuery.error ? JSON.stringify(searchQuery.error, Object.getOwnPropertyNames(searchQuery.error as object), 2) : undefined}
          onRetry={() => searchQuery.refetch()}
        />
      )}

      {(searchQuery.isLoading || (result && debouncedQuery.length >= 2)) && (
        <div className="space-y-3">
          {result && (
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{result.totalResults} Ergebnisse gefunden (zeige {result.firstResult}–{result.lastResult})</span>
              <div className="flex gap-2">
                {hasPrev && (
                  <button onClick={() => setStartRow(Math.max(1, startRow - 50))} className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs">
                    ← Zurück
                  </button>
                )}
                {hasMore && (
                  <button onClick={() => setStartRow(result.lastResult + 1)} className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs">
                    Weiter →
                  </button>
                )}
              </div>
            </div>
          )}

          <DataTable<EgisSearchItem>
            data={result?.items ?? []}
            columns={searchColumns}
            getRowId={(i) => i.proprietaryProductNumber}
            loading={searchQuery.isLoading}
            emptyState={
              <EmptyState
                title="Keine Ergebnisse"
                description="Passe die Suche an oder entferne Filter."
              />
            }
            enableSorting={false}
            searchable={false}
            // Inline expandable rows — detail panel opens directly under the article
            getRowCanExpand={() => true}
            renderExpandedRow={renderExpandedRow}
            expandedRowIds={expandedRowIds}
            onExpandedChange={(id, expanded) => {
              setSelectedProduct(expanded ? id : null)
              setSapResult(null)
            }}
            onRowClick={(item) => {
              setSelectedProduct((prev) => (prev === item.proprietaryProductNumber ? null : item.proprietaryProductNumber))
              setSapResult(null)
            }}
          />
        </div>
      )}

      {/* ── Fallback detail panel for accessories not in the current search results ── */}
      {selectedProduct && !selectedItemInResults && (
        <>
          {(priceQuery.isLoading || specQuery.isLoading) && (
            <div className="flex items-center justify-center py-6 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Daten werden geladen...
            </div>
          )}
          {(spec || priceData) && !(priceQuery.isLoading && specQuery.isLoading) && (
            <EgisDetailPanel
              item={null}
              spec={spec}
              priceData={priceData}
              priceLoading={priceQuery.isLoading}
              specLoading={specQuery.isLoading}
              onCreateInSap={() => { setSapResult(null); setConfirmSap(true) }}
              sapResult={sapResult}
              onSelectAccessory={(pn) => { setSelectedProduct(pn); setSapResult(null) }}
            />
          )}
        </>
      )}

      {/* ── SAP confirmation dialog ───────────────────────────────────────── */}
      {spec && (
        <ConfirmDialog
          open={confirmSap}
          title="Artikel in SAP anlegen"
          variant="default"
          confirmLabel="Anlegen"
          cancelLabel="Abbrechen"
          loading={createInSapMutation.isPending}
          message={
            <div className="space-y-2 text-sm text-gray-700">
              <p>Folgender Artikel wird als neuer Stammdaten-Artikel in SAP Business One angelegt:</p>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-xs font-mono">
                <div><span className="text-gray-500">Artikel-Nr.:</span> {spec.productNumber}</div>
                <div><span className="text-gray-500">Name:</span> {spec.description.slice(0, 100)}</div>
                {selectedItem?.recommendedRetailPrice != null && (
                  <div><span className="text-gray-500">VK-Preis:</span> {selectedItem.recommendedRetailPrice.toFixed(2)} €</div>
                )}
              </div>
              <p className="text-xs text-gray-500">Die Artikel-Nummer wird aus der EGIS Produktnummer übernommen. Falls der Artikel bereits in SAP existiert, schlägt die Anlage fehl.</p>
            </div>
          }
          onConfirm={() => createInSapMutation.mutate({
            productNumber: spec.productNumber,
            salesPrice: selectedItem?.recommendedRetailPrice ?? undefined,
          })}
          onCancel={() => setConfirmSap(false)}
        />
      )}
    </div>
  )
}

// ─── Detail Panel ───────────────────────────────────────────────────────────

interface EgisDetailPanelProps {
  item: EgisSearchItem | null
  spec: EgisProductSpecification | null
  priceData: EgisPriceAndAvailability | null
  priceLoading: boolean
  specLoading: boolean
  onCreateInSap: () => void
  sapResult: { success: boolean; itemCode?: string; message?: string } | null
  onSelectAccessory: (productNumber: string) => void
}

function EgisDetailPanel({ item, spec, priceData, priceLoading, specLoading, onCreateInSap, sapResult, onSelectAccessory }: EgisDetailPanelProps) {
  const [showAllFeatures, setShowAllFeatures] = useState(false)

  const displaySpec = spec ?? null
  const description = displaySpec?.description ?? item?.description ?? ''
  const manufacturerName = displaySpec?.manufacturerName ?? item?.manufacturerName ?? ''
  const productNumber = displaySpec?.productNumber ?? item?.proprietaryProductNumber ?? ''
  const ean = displaySpec?.globalProductNumber ?? item?.globalProductNumber ?? null
  const imageUrl = displaySpec?.imageUrl ?? item?.imageUrl ?? null

  const features = displaySpec?.features ?? []
  const visibleFeatures = showAllFeatures ? features : features.slice(0, 8)

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 p-4 border-b border-gray-100">
        <div className="flex items-start gap-3 min-w-0">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={description}
              className="w-16 h-16 object-contain rounded border border-gray-100 flex-shrink-0 bg-white"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
          )}
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 leading-snug">{description}</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {manufacturerName} · {productNumber}
              {ean && <> · <span className="font-mono">EAN: {ean}</span></>}
            </p>
            {displaySpec?.descriptions.short && (
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{displaySpec.descriptions.short}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {displaySpec?.datasheetPdfUrl && (
            <a
              href={displaySpec.datasheetPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              <FileText className="w-3.5 h-3.5" />
              Datenblatt
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <Button
            size="sm"
            variant="primary"
            onClick={onCreateInSap}
            disabled={specLoading}
          >
            <PackagePlus className="w-3.5 h-3.5 mr-1" />
            In SAP anlegen
          </Button>
        </div>
      </div>

      {/* SAP result feedback */}
      {sapResult && (
        <div className={cn(
          'mx-4 mt-3 px-3 py-2 rounded-lg text-sm flex items-start gap-2',
          sapResult.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200',
        )}>
          {sapResult.success
            ? <><span className="font-medium">✓</span> {sapResult.message}</>
            : <><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{sapResult.message}</span></>}
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Technical features */}
        {specLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="w-3 h-3 animate-spin" /> Technische Daten werden geladen...
          </div>
        )}

        {!specLoading && features.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Technische Daten</h5>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              {visibleFeatures.map((f, i) => (
                <div key={i} className="flex items-baseline gap-1 text-xs py-0.5 border-b border-gray-50">
                  <span className="text-gray-500 min-w-0 truncate flex-1" title={f.key}>{f.key}</span>
                  <span className="text-gray-900 font-medium text-right flex-shrink-0 max-w-[50%] truncate" title={f.value}>{f.value}</span>
                </div>
              ))}
            </div>
            {features.length > 8 && (
              <button
                onClick={() => setShowAllFeatures(!showAllFeatures)}
                className="mt-2 text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1"
              >
                {showAllFeatures
                  ? <><ChevronUp className="w-3 h-3" /> Weniger anzeigen</>
                  : <><ChevronDown className="w-3 h-3" /> Alle {features.length} Merkmale anzeigen</>}
              </button>
            )}
          </div>
        )}

        {/* Accessories */}
        {!specLoading && spec && spec.accessories.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5" />
              Zubehör ({spec.accessories.length})
            </h5>
            <div className="flex flex-wrap gap-1.5">
              {spec.accessories.map((pn) => (
                <button
                  key={pn}
                  onClick={() => onSelectAccessory(pn)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono bg-gray-50 hover:bg-brand-50 border border-gray-200 hover:border-brand-300 rounded text-gray-700 hover:text-brand-700 transition-colors"
                  title="Zubehör-Details anzeigen"
                >
                  {pn}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Distributor pricing */}
        {priceLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="w-3 h-3 animate-spin" /> Preise & Verfügbarkeit werden geladen...
          </div>
        )}

        {!priceLoading && priceData && (
          <EgisPriceDetail product={priceData} />
        )}
      </div>
    </div>
  )
}

// ─── Price Detail ────────────────────────────────────────────────────────────

function offerId(o: EgisDistributorOffer) {
  return `${o.distributorId}-${o.distributorProductNumber}`
}

function EgisPriceDetail({ product }: { product: EgisPriceAndAvailability }) {
  const offers = useMemo(
    () => [...product.distributorOffers].sort((a, b) =>
      (a.purchasePrice ?? Infinity) - (b.purchasePrice ?? Infinity)
    ),
    [product.distributorOffers],
  )

  const bestStockedOffer = offers.find((o) => (o.availableQuantity ?? 0) > 0) ?? null
  const hasAnyStock = bestStockedOffer !== null

  // Row highlight winners
  const bestPriceOffer = useMemo(() => {
    const candidates = offers.filter((o) => o.purchasePrice != null)
    if (candidates.length === 0) return null
    return candidates.reduce((best, o) => (o.purchasePrice! < best.purchasePrice! ? o : best))
  }, [offers])

  const bestPriceWithShippingOffer = useMemo(() => {
    const candidates = offers.filter(
      (o) => o.purchasePrice != null && o.deliveryInfo?.freightCharges != null,
    )
    if (candidates.length === 0) return null
    return candidates.reduce((best, o) => {
      const total = o.purchasePrice! + o.deliveryInfo!.freightCharges!
      const bestTotal = best.purchasePrice! + best.deliveryInfo!.freightCharges!
      return total < bestTotal ? o : best
    })
  }, [offers])

  const fastestDeliveryOffer = useMemo(() => {
    const candidates = offers.filter(
      (o) => (o.deliveryInfo?.standardDeliveryTimeHours ?? null) != null,
    )
    if (candidates.length === 0) return null
    return candidates.reduce((best, o) =>
      o.deliveryInfo!.standardDeliveryTimeHours! < best.deliveryInfo!.standardDeliveryTimeHours!
        ? o
        : best,
    )
  }, [offers])

  const getRowHighlight = (offer: EgisDistributorOffer): string | undefined => {
    if ((offer.availableQuantity ?? 0) === 0) return 'opacity-50'
    const id = offerId(offer)
    if (bestPriceWithShippingOffer && offerId(bestPriceWithShippingOffer) === id)
      return '!bg-purple-50'
    if (bestPriceOffer && offerId(bestPriceOffer) === id)
      return '!bg-green-50'
    if (fastestDeliveryOffer && offerId(fastestDeliveryOffer) === id)
      return '!bg-blue-50'
    return undefined
  }

  const bestPriceId = bestPriceOffer ? offerId(bestPriceOffer) : null
  const bestPriceWithShippingId = bestPriceWithShippingOffer ? offerId(bestPriceWithShippingOffer) : null
  const fastestDeliveryId = fastestDeliveryOffer ? offerId(fastestDeliveryOffer) : null

  const columns = useMemo<ColumnDef<EgisDistributorOffer, unknown>[]>(
    () => [
      {
        accessorKey: 'distributorName',
        header: 'Distri',
        size: 170,
        cell: ({ row }) => {
          const id = offerId(row.original)
          const badge =
            bestPriceWithShippingId === id
              ? { label: 'Bester Preis inkl. Fracht', cls: 'bg-purple-100 text-purple-700' }
              : bestPriceId === id
              ? { label: 'Bester Preis', cls: 'bg-green-100 text-green-700' }
              : fastestDeliveryId === id
              ? { label: 'Schnellste Lieferung', cls: 'bg-blue-100 text-blue-700' }
              : null
          return (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-medium text-gray-900">{row.original.distributorName}</span>
              {row.original.eol && <span className="text-[10px] bg-red-100 text-red-700 px-1 rounded">EOL</span>}
              {badge && (
                <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap', badge.cls)}>
                  {badge.label}
                </span>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'distributorProductNumber',
        header: 'Artikel-Nr.',
        size: 130,
        cell: ({ row }) => <span className="text-xs text-gray-500 font-mono">{row.original.distributorProductNumber}</span>,
      },
      {
        accessorKey: 'availableQuantity',
        header: 'Bestand',
        size: 75,
        cell: ({ row }) => {
          const q = row.original.availableQuantity
          return (
            <span
              className={cn(
                'block text-right font-mono text-sm',
                q > 10 ? 'text-green-600' : q > 0 ? 'text-amber-600' : 'text-red-500',
              )}
            >
              {q}
            </span>
          )
        },
      },
      {
        accessorKey: 'purchasePrice',
        header: 'EK',
        size: 90,
        cell: ({ row }) => (
          <span className="block text-right font-mono font-medium text-gray-900">
            {row.original.purchasePrice != null ? `${row.original.purchasePrice.toFixed(2)} €` : '–'}
          </span>
        ),
      },
      {
        id: 'freight',
        header: 'Fracht',
        size: 85,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="block text-right font-mono text-xs text-gray-500">
            {row.original.deliveryInfo?.freightCharges != null
              ? `${row.original.deliveryInfo.freightCharges.toFixed(2)} €`
              : '–'}
          </span>
        ),
      },
      {
        id: 'delivery',
        header: 'Lieferdatum',
        size: 120,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.deliveryInfo ? (
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <Truck className="w-3 h-3 text-gray-400 flex-shrink-0" />
              {row.original.deliveryInfo.deliveryDateText ||
                (row.original.deliveryInfo.standardDeliveryTimeHours
                  ? `${row.original.deliveryInfo.standardDeliveryTimeHours}h`
                  : '–')}
            </div>
          ) : (
            <span className="text-xs text-gray-400">–</span>
          ),
      },
      {
        id: 'totalPrice',
        header: 'Gesamtpreis',
        size: 105,
        enableSorting: false,
        cell: ({ row }) => {
          const ek = row.original.purchasePrice
          const fracht = row.original.deliveryInfo?.freightCharges ?? null
          if (ek == null) return <span className="block text-right font-mono text-xs text-gray-400">–</span>
          const total = ek + (fracht ?? 0)
          return (
            <span className="block text-right font-mono font-medium text-gray-900">
              {total.toFixed(2)} €
            </span>
          )
        },
      },
      {
        accessorKey: 'recommendSellingPrice',
        header: 'Empf. VK',
        size: 95,
        cell: ({ row }) => (
          <span className="block text-right font-mono text-gray-600">
            {row.original.recommendSellingPrice != null ? `${row.original.recommendSellingPrice.toFixed(2)} €` : '–'}
          </span>
        ),
      },
    ],
    [bestPriceId, bestPriceWithShippingId, fastestDeliveryId],
  )

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Preise &amp; Verfügbarkeit
        </h5>
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-blue-100 border border-blue-200" />
            Schnellste Lieferung
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-green-100 border border-green-200" />
            Bester Preis
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-purple-100 border border-purple-200" />
            Bester Preis inkl. Fracht
          </span>
          <span className="font-normal">{product.distributorOffers.length} Distributoren</span>
        </div>
      </div>

      {/* Combined summary bar: availability (left) + savings (right) */}
      {(() => {
        const prices = product.distributorOffers
          .map((o) => o.purchasePrice)
          .filter((p): p is number => p != null)
        const saving = prices.length >= 2 ? Math.max(...prices) - Math.min(...prices) : 0
        const savingPct = prices.length >= 2 ? (saving / Math.max(...prices)) * 100 : 0

        if (hasAnyStock) {
          return (
            <div className="flex items-center justify-between gap-4 text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
              <span className="flex items-center gap-2 min-w-0">
                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  Bester EK (lagernd):{' '}
                  <span className="font-bold">
                    {bestStockedOffer.purchasePrice != null ? `${bestStockedOffer.purchasePrice.toFixed(2)} €` : '–'}
                  </span>
                  {' '}via {bestStockedOffer.distributorName} · {bestStockedOffer.availableQuantity} Stk. verfügbar
                </span>
              </span>
              {saving > 0 && (
                <span className="flex items-center gap-1 flex-shrink-0 text-green-600">
                  <TrendingDown className="w-3.5 h-3.5" />
                  Ersparnis: <span className="font-bold ml-1">{saving.toFixed(2)} €</span>
                  <span className="text-green-500">({savingPct.toFixed(1)}%)</span>
                </span>
              )}
            </div>
          )
        }

        return (
          <div className="flex items-center justify-between gap-4 text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700">
            <span className="flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Nicht lagernd – kein Distributor führt diesen Artikel derzeit auf Lager.
            </span>
            {saving > 0 && (
              <span className="flex items-center gap-1 flex-shrink-0 text-green-600">
                <TrendingDown className="w-3.5 h-3.5" />
                Ersparnis: <span className="font-bold ml-1">{saving.toFixed(2)} €</span>
                <span className="text-green-500">({savingPct.toFixed(1)}%)</span>
              </span>
            )}
          </div>
        )
      })()}

      <DataTable<EgisDistributorOffer>
        data={offers}
        columns={columns}
        getRowId={(o) => `${o.distributorId}-${o.distributorProductNumber}`}
        enableSorting={false}
        searchable={false}
        rowClassName={getRowHighlight}
        emptyState={<EmptyState size="sm" title="Keine Angebote" />}
      />
    </div>
  )
}
